/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {Link} from '@salesforce/retail-react-app/app/components/shared/ui'
import {formatPhoneNumber} from '@salesforce/retail-react-app/app/utils/phone-utils'
import React from 'react'
import {useIntl} from 'react-intl'
import {languageAliases} from '../../constants'
import {validatePassword} from '../../utils/password-utils'
import {provinceOptions} from '../forms/state-province-options'

export default function useRegistrationFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            placeholder: formatMessage({
                defaultMessage: 'First Name',
                id: 'use_registration_fields.label.first_name'
            }),
            type: 'text',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_registration_fields.error.required_first_name'
                })
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            placeholder: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_registration_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_registration_fields.error.required_last_name'
                })
            },
            error: errors[`${prefix}lastName`],
            control
        },
        email: {
            name: `${prefix}email`,
            placeholder: formatMessage({
                defaultMessage: 'Email',
                id: 'use_registration_fields.label.email'
            }),
            type: 'email',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid email address.',
                    id: 'use_registration_fields.error.required_email'
                })
            },
            error: errors[`${prefix}email`],
            control
        },
        password: {
            name: `${prefix}password`,
            placeholder: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_registration_fields.label.password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please create a password.',
                    id: 'use_registration_fields.error.required_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least 8 characters.',
                            id: 'use_registration_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one uppercase letter.',
                            id: 'use_registration_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one lowercase letter.',
                            id: 'use_registration_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one number.',
                            id: 'use_registration_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one special character.',
                            id: 'use_registration_fields.error.special_character'
                        }),
                    hasNoSpace: (val) =>
                        validatePassword(val).hasNoSpace ||
                        formatMessage({
                            defaultMessage: 'Not Include spaces.',
                            id: 'use_registration_fields.error.contain_number'
                        })
                }
            },

            error: errors[`${prefix}password`],
            control
        },
        confirmPassword: {
            name: `${prefix}confirmPassword`,
            placeholder: 'Confirm Password',
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please create a password.',
                    id: 'use_registration_fields.error.required_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least 8 characters.',
                            id: 'use_registration_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one uppercase letter.',
                            id: 'use_registration_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one lowercase letter.',
                            id: 'use_registration_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one number.',
                            id: 'use_registration_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one special character.',
                            id: 'use_registration_fields.error.special_character'
                        }),
                    hasNoSpace: (val) =>
                        validatePassword(val).hasNoSpace ||
                        formatMessage({
                            defaultMessage: 'Not Include spaces.',
                            id: 'use_registration_fields.error.contain_number'
                        })
                }
            },

            error: errors[`${prefix}confirmPassword`],
            control
        },
        mobileNumber: {
            name: `${prefix}mobileNumber`,
            placeholder: formatMessage({
                defaultMessage: 'Mobile Number (optional)',
                id: 'use_registration_fields.label.mobile_number'
            }),
            messages: {
                phoneNumberRequired: formatMessage({
                    defaultMessage: 'Please enter your phone number.',
                    id: 'use_registration_fields.error.mobile_number'
                })
            },
            type: 'tel',
            defaultValue: '',
            error: errors[`${prefix}mobileNumber`],
            inputProps: ({onChange}) => ({
                inputMode: 'numeric',
                onChange(evt) {
                    onChange(formatPhoneNumber(evt.target.value))
                }
            }),
            control
        },
        province: {
            name: `${prefix}province`,
            placeholder: formatMessage({
                defaultMessage: 'Mobile Number (optional)',
                id: 'use_registration_fields.label.province'
            }),
            defaultValue: '',
            type: 'select',
            options: [...provinceOptions],
            rules: {
                required: formatMessage(
                    {
                        defaultMessage:
                            'Please select your province or territory.',
                        id: 'use_address_fields.error.please_select_your_state_or_province'
                    },
                    {
                        stateOrProvince: 'province'
                    }
                )
            },

            error: errors[`${prefix}province`],
            control
        },
        language: {
            name: `${prefix}language`,
            placeholder: formatMessage({
                defaultMessage: 'Preferred Language',
                id: 'use_registration_fields.label.preferred_language'
            }),
            type: 'select',
            options: [
                {
                    value: languageAliases.en.locale,
                    label: languageAliases.en.name
                },
                {
                    value: languageAliases.fr.locale,
                    label: languageAliases.fr.name
                }
            ],
            rules: {
                required: formatMessage(
                    {
                        defaultMessage: 'Please Select a language.',
                        id: 'use_address_fields.error.please_select_a_language'
                    },
                    {
                        language: 'language'
                    }
                )
            },
            error: errors[`${prefix}language`],
            control
        },

        acceptsMarketing: {
            name: `${prefix}acceptsMarketing`,
            label: formatMessage({
                defaultMessage:
                    'I agree, Rennaï can send me emails with updates and special offers. (I can unsubscribe at any time.)',
                id: 'use_registration_fields.label.sign_up_to_emails'
            }),
            type: 'checkbox',
            defaultValue: false,
            error: errors[`${prefix}acceptsMarketing`],
            control
        },
        agreeToUseOfInformation: {
            name: `${prefix}agreeToUseOfInformation`,
            label: formatMessage({
                defaultMessage:
                    'I agree, Rennaï can use my information to personalize my experience and recommend products and services and to tailor my user experience.',
                id: 'use_registration_fields.label.agree_to_use_of_information'
            }),
            type: 'checkbox',
            defaultValue: false,
            error: errors[`${prefix}agreeToUseOfInformation`],
            control
        },
        agreeToTerms: {
            name: `${prefix}agreeToTerms`,
            label: formatMessage(
                {
                    defaultMessage:
                        'By signing up, I’m agreeing to Rennaï’s <terms>Terms & Conditions</terms>, <teams>Membership Terms</teams> and <policy>Privacy Policy</policy>.',
                    id: 'use_registration_fields.label.agree_to_terms'
                },
                {
                    terms: (chunks) => (
                        <Link
                            href="/pages/legals?section=termsOfUse"
                            textDecoration="underline"
                            isExternal
                        >
                            {chunks}
                        </Link>
                    ),
                    teams: (chunks) => (
                        <Link
                            href="/pages/legals?section=membershipTerms"
                            textDecoration="underline"
                            isExternal
                        >
                            {chunks}
                        </Link>
                    ),
                    policy: (chunks) => (
                        <Link
                            href="/pages/legals?section=privacyPolicy"
                            textDecoration="underline"
                            isExternal
                        >
                            {chunks}
                        </Link>
                    )
                }
            ),
            type: 'checkbox',
            defaultValue: false,
            error: errors[`${prefix}agreeToTerms`],
            control
        },
        emailConsent: {
            name: `${prefix}emailConsent`,
            label: formatMessage({
                defaultMessage:
                    'I agree to receive emails from my self-care advisors',
                id: 'use_registration_fields.label.agree_receive_emails'
            }),
            options: [
                {
                    label: formatMessage({
                        defaultMessage: 'Yes',
                        id: 'use_registration_fields.label.options_yes'
                    }),
                    value: true,
                    width: '16px',
                    height: '16px'
                },
                {
                    label: formatMessage({
                        defaultMessage: 'No',
                        id: 'use_registration_fields.label.options_no'
                    }),
                    value: false,
                    width: '16px',
                    height: '16px'
                }
            ],
            type: 'radio',
            defaultValue: null,
            error: errors[`${prefix}emailConsent`],
            control
        },
        messagesConsent: {
            name: `${prefix}messagesConsent`,
            label: formatMessage({
                defaultMessage:
                    'I agree to receive messages from your self-care advisors',
                id: 'use_registration_fields.label.agree_receive_messages'
            }),
            options: [
                {
                    label: formatMessage({
                        defaultMessage: 'Yes',
                        id: 'use_registration_fields.label.options_yes'
                    }),
                    value: true,
                    width: '16px',
                    height: '16px'
                },
                {
                    label: formatMessage({
                        defaultMessage: 'No',
                        id: 'use_registration_fields.label.options_no'
                    }),
                    value: false,
                    width: '16px',
                    height: '16px'
                }
            ],
            type: 'radio',
            defaultValue: null,
            error: errors[`${prefix}messagesConsent`],
            control
        },

        phoneConsent: {
            name: `${prefix}phoneConsent`,
            label: formatMessage({
                defaultMessage:
                    'I agree to receive phone calls from your self-care advisors',
                id: 'use_registration_fields.label.agree_receive_calls'
            }),
            options: [
                {
                    label: formatMessage({
                        defaultMessage: 'Yes',
                        id: 'use_registration_fields.label.options_yes'
                    }),
                    value: true,
                    width: '16px',
                    height: '16px'
                },
                {
                    label: formatMessage({
                        defaultMessage: 'No',
                        id: 'use_registration_fields.label.options_no'
                    }),
                    value: false,
                    width: '16px',
                    height: '16px'
                }
            ],
            type: 'radio',
            defaultValue: null,
            error: errors[`${prefix}phoneConsent`],
            control
        },

        dateOfBirth: {
            name: `${prefix}dateOfBirth`,
            messages: {
                dateOfBirthRequired: formatMessage({
                    defaultMessage: 'Please enter your date of birth.',
                    id: 'use_registration_fields.error.date_of_birth'
                })
            },
            type: 'date',
            defaultValue: '',
            error: errors[`${prefix}dateOfBirth`],
            control
        }
    }

    return fields
}
